import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Linkedin, Instagram, Facebook, Twitter } from "lucide-react";
import { SITE, IMAGES, activeSocials } from "../config/site";
import { PRACTICE_AREAS } from "../data/content";
import { useConfig } from "../context/ConfigContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Our Team", to: "/team" },
  { label: "Services", to: "/services" },
  { label: "Client Reviews", to: "/reviews" },
  { label: "Contact", to: "/contact" },
];

const SOCIAL_ICONS = { linkedin: Linkedin, instagram: Instagram, facebook: Facebook, twitter: Twitter };

export function Footer() {
  const { api } = useConfig();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const socials = activeSocials();

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please enter your first name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${api}/newsletter`, { firstName, email, website });
      toast.success("Thank you for your interest.");
      setFirstName(""); setEmail("");
    } catch (err) {
      const msg = err?.response?.status === 503
        ? "Newsletter sign-up is not configured yet. Please check back soon."
        : "We were unable to submit your interest. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-navy text-white/80" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.3fr]">
          {/* Brand */}
          <div>
            <span className="inline-flex rounded-sm bg-white px-3 py-2">
              <img src={IMAGES.logo} alt="Nolen Law" className="h-12 w-auto" />
            </span>
            <p className="mt-5 font-serif text-xl font-500 leading-snug text-white">{SITE.firmName}</p>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/60">
              Strategic, ethical legal counsel rooted in {SITE.location}.
            </p>
            {socials.length > 0 && (
              <div className="mt-6 flex gap-3" data-testid="footer-socials">
                {socials.map(([key, url]) => {
                  const Icon = SOCIAL_ICONS[key];
                  return (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                      aria-label={key} className="rounded-sm border border-white/20 p-2 transition-colors hover:border-gold hover:text-gold">
                      {Icon && <Icon className="h-4 w-4" strokeWidth={1.6} />}
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Navigate */}
          <div>
            <h4 className="text-xs font-600 uppercase tracking-label text-gold">Navigate</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {NAV.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/70 transition-colors hover:text-white" data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g,"-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Practice areas */}
          <div>
            <h4 className="text-xs font-600 uppercase tracking-label text-gold">Practice Areas</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {PRACTICE_AREAS.slice(0, 8).map((p) => (
                <li key={p.anchor}>
                  <Link to={`/services#${p.anchor}`} className="text-white/70 transition-colors hover:text-white" data-testid={`footer-practice-${p.anchor}`}>
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + newsletter */}
          <div>
            <h4 className="text-xs font-600 uppercase tracking-label text-gold">Office</h4>
            <ul className="mt-5 space-y-3 text-sm text-white/75" data-testid="footer-contact">
              <li>
                <a href={`tel:${SITE.phoneRaw}`} data-testid="footer-phone"
                  className="transition-colors hover:text-white">
                  {SITE.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.publicEmail}`} data-testid="footer-email"
                  className="break-all transition-colors hover:text-white">
                  {SITE.publicEmail}
                </a>
              </li>
              <li>
                <address className="not-italic leading-relaxed">
                  {SITE.addressLines.map((line) => (<div key={line}>{line}</div>))}
                </address>
              </li>
              <li>
                <div className="leading-relaxed">
                  {SITE.officeHoursLines.map((line) => (<div key={line}>{line}</div>))}
                </div>
              </li>
            </ul>

            <h4 className="mt-8 text-xs font-600 uppercase tracking-label text-gold">Stay Informed</h4>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Occasional legal insights for business owners, entrepreneurs, athletes, creators, and property owners.
            </p>
            <form onSubmit={submitNewsletter} className="mt-4 space-y-2" data-testid="newsletter-form">
              <input
                type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name" aria-label="First Name" data-testid="newsletter-first-name"
                className="w-full rounded-sm border border-white/20 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
              />
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address" aria-label="Email Address" data-testid="newsletter-email"
                className="w-full rounded-sm border border-white/20 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
              />
              {/* Honeypot */}
              <input type="text" tabIndex={-1} autoComplete="off" value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="hidden" aria-hidden="true" />
              <button
                type="submit" disabled={submitting} data-testid="newsletter-submit"
                className="w-full rounded-sm bg-gold px-4 py-2.5 text-sm font-600 text-navy transition-colors hover:bg-gold-soft disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Stay Informed"}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <p className="max-w-3xl text-xs leading-relaxed text-white/50" data-testid="footer-disclaimer">
            {SITE.disclaimer}
          </p>
          <p className="mt-5 text-xs text-white/40">
            © {new Date().getFullYear()} {SITE.firmName}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
